// PBIN product provisioning wizard (DEV-1428) — shared types.
// Launched from Client-Products when a client adopts a PBIN product it never had.
// Clients and users are managed by the Portal's normal flows.

export const PBIN_S_PRODUCT_TYPE_ID = "d7a4437f-fde9-48cb-bd72-622756c795c2";
export const PBIN_R_PRODUCT_TYPE_ID = "390a999d-0294-434f-8882-d691d6cb9041";

export function isPbinProductType(productTypeId?: string): boolean {
  const id = (productTypeId || "").toLowerCase();
  return id === PBIN_S_PRODUCT_TYPE_ID || id === PBIN_R_PRODUCT_TYPE_ID;
}

export interface WizardClient {
  clientGuid: string;
  name: string;
}

export interface WizardProduct {
  productGuid: string;
  name: string;
  subName?: string;
  productTypeId: string;
}

export interface ProductConfig {
  databaseServer: string;
  databaseCatalog: string;
  /** Azure SQL elastic pool the new database is created in (dev/test).
   * Empty = standalone database (VM-hosted or local SQL Server). */
  elasticPoolName: string;
  databaseUser: string;
  databasePassword: string;
  authDatabaseServer: string;
  authDatabaseCatalog: string;
  authDatabaseUser: string;
  authDatabasePassword: string;
  storageTypeId: number;
  storagePath: string;
  storageServerUserName: string;
  storageServerUserPassword: string;
  incomingHandlerEmailServer: string;
  incomingHandlerEmailUser: string;
  incomingHandlerEmailPassword: string;
  incomingHandlerConnectionPort: string;
  gabFlavor: "AzureSql" | "LocalStub" | "SqlServerVm";
  /** dbcreator login used only for this run - relayed to PBIN, never stored.
   * Empty = integrated security (local dev). */
  provisioningSqlUser: string;
  provisioningSqlPassword: string;
}

export interface FirmProfileData {
  companyName: string;
  dtcNo: string;
  institutionNo: string;
  taxId: string;
  agentBankNo: string;
  mpid: string;
  contactTitle: string;
  contactFirstName: string;
  contactLastName: string;
  departmentName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phoneNo: string;
  faxNo: string;
  emailBackTo: string;
  primarySignorSignatureImagePath: string;
}

export interface AccountTypeEntry {
  name: string;
  abbreviation: string;
}

export interface AssociatedDtcEntry {
  dtcNumber: string;
  institutionNo: string;
  mpid: string;
  companyName: string;
  agentBank: string;
  bid: string;
  isDefault: boolean;
}

export interface ProvisionWizardFormData {
  config: ProductConfig;
  firmProfile: FirmProfileData;
  accountTypes: AccountTypeEntry[];
  associatedDtcs: AssociatedDtcEntry[];
}

export interface ProvisioningStepResult {
  step: string;
  success: boolean;
  error?: string;
}

export interface ProvisionedProductResult {
  productGuid: string;
  productName?: string;
  provisioned: boolean;
  configurationSaved: boolean;
  enabled: boolean;
  provisioning?: {
    success: boolean;
    databaseName?: string;
    steps: ProvisioningStepResult[];
    transportError?: string;
  };
  error?: string;
}

export interface ProvisionClientProductsResult {
  success: boolean;
  clientGuid: string;
  clientName: string;
  products: ProvisionedProductResult[];
}

export const emptyProductConfig = (databaseCatalog: string): ProductConfig => ({
  databaseServer: "",
  databaseCatalog,
  elasticPoolName: "",
  databaseUser: "",
  databasePassword: "",
  authDatabaseServer: "",
  authDatabaseCatalog: "",
  authDatabaseUser: "",
  authDatabasePassword: "",
  storageTypeId: 1,
  storagePath: "",
  storageServerUserName: "",
  storageServerUserPassword: "",
  incomingHandlerEmailServer: "",
  incomingHandlerEmailUser: "",
  incomingHandlerEmailPassword: "",
  incomingHandlerConnectionPort: "",
  gabFlavor: "AzureSql",
  provisioningSqlUser: "",
  provisioningSqlPassword: "",
});

export const emptyFirmProfile: FirmProfileData = {
  companyName: "",
  dtcNo: "",
  institutionNo: "",
  taxId: "",
  agentBankNo: "",
  mpid: "",
  contactTitle: "",
  contactFirstName: "",
  contactLastName: "",
  departmentName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  phoneNo: "",
  faxNo: "",
  emailBackTo: "",
  primarySignorSignatureImagePath: "",
};

export const defaultProvisionWizardFormData: ProvisionWizardFormData = {
  config: emptyProductConfig(""),
  firmProfile: { ...emptyFirmProfile },
  accountTypes: [],
  associatedDtcs: [],
};

/** Suggested database name following the existing dev convention. */
export function suggestDatabaseCatalog(productTypeId: string, clientName: string): string {
  const side = productTypeId.toLowerCase() === PBIN_S_PRODUCT_TYPE_ID ? "S" : "R";
  const compact = clientName.replace(/[^A-Za-z0-9]/g, "");
  return `E2ESSO_PBIN-${side}_Client_${compact}`;
}
